<template>
  <div id="call_analytics_grid">
    <InfoGrid :columns="3">
      <ContainerInfoItem
        v-for="item in dealAnalyticsData"
        :key="item.id"
        source="deal_analytics"
        :title="item.title"
        :description="range.label"
        :value="getFormattedValue(item)"
        :is-loading="item.isLoading"
        :show-tooltip="item.id === 'pipeline_value'"
        :full-value="getFullValue(item)"
      />
    </InfoGrid>
  </div>
</template>

<script setup>
import { computed, ref, watchEffect } from "vue";
import store from "@/src/store/index.js";
import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import AnalyticService from "@/src/api/AnalyticService.js";
import { formatAnalyticsValue } from "@/src/utils/analyticUtils.js";
import { formatNumber, formatValue } from "@/src/utils/utils.js";
import { units } from "@/src/constants/analyticConstants.js";
import { isNumber } from "lodash";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";

const props = defineProps({
  primaryCurrencySymbol: { type: String, default: "$" },
});

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const analyticsConfig = [
  {
    id: "pipeline_value",
    title: "Total Pipeline Value",
    fetchFn: AnalyticService.V2.fetchTotalPipelineValue,
    dataKey: "total_pipeline_value",
    unit: units.AMOUNT,
  },
  {
    id: "deals_won",
    title: "Deals won",
    fetchFn: AnalyticService.V2.fetchDealCount,
    dataKey: "count",
    extraParams: { action: "won" },
  },
  {
    id: "deals_lost",
    title: "Deals lost",
    fetchFn: AnalyticService.V2.fetchDealCount,
    dataKey: "count",
    extraParams: { action: "lost" },
  },
  {
    id: "average_deal_age",
    title: "Average time to close deals",
    fetchFn: AnalyticService.V2.fetchAverageDealAge,
    dataKey: "average_deal_age_days",
    unit: units.DAY,
  },
  {
    id: "win_lost_ratio",
    title: "Win rate",
    fetchFn: AnalyticService.V2.fetchDealWinLostRatio,
    dataKey: "win_lost_ratio",
    unit: units.RATE,
  },
  {
    id: "lost_ratio",
    title: "Loss rate",
    fetchFn: AnalyticService.V2.fetchDealWinLostRatio,
    dataKey: "win_lost_ratio",
    unit: units.RATE,
  },
];

const dealAnalyticsData = ref(
  analyticsConfig.map((config) => ({
    id: config.id,
    title: config.title,
    value: null,
    isLoading: true,
    unit: config.unit,
  })),
);

const fetchAnalytics = async () => {
  dealAnalyticsData.value.forEach((item) => {
    item.isLoading = true;
  });

  const baseParams = {
    deal_owner_id: userIds.value,
    team_id: teamIds.value,
    pipeline_id: filters.value?.pipeline?.id,
    start_time: range.value?.start,
    end_time: range.value?.end,
  };

  const promises = analyticsConfig.map(async (config) => {
    try {
      const params = { ...baseParams, ...config.extraParams };
      const response = await config.fetchFn(params);

      if (response?.success) {
        const item = dealAnalyticsData.value.find(
          (item) => item.id === config.id,
        );
        if (item) {
          const apiValue = response.data[config.dataKey];

          if (item.id === "lost_ratio" && isNumber(apiValue)) {
            const totalClosedDeals = response.data?.total_closed_deals || 0;
            item.value = totalClosedDeals > 0 ? 100 - Number(apiValue) : 0;
          } else {
            item.value = apiValue;
          }

          item.isLoading = false;
        }
      }
    } catch (error) {
      console.error(`Error fetching ${config.id}:`, error);
      const item = dealAnalyticsData.value.find(
        (item) => item.id === config.id,
      );
      if (item) {
        item.value = null;
        item.isLoading = false;
      }
    }
  });

  await Promise.all(promises);
};

const getFormattedValue = (item) => {
  if (item.id === "pipeline_value") {
    return `${props.primaryCurrencySymbol}${formatNumber(item.value)}`;
  }
  return formatAnalyticsValue(item.value, item.unit, props.primaryCurrencySymbol);
};

const getFullValue = (item) => {
  if (item.id === "pipeline_value") {
    return `${props.primaryCurrencySymbol}${formatValue(item.value)}`;
  }
  return null;
};

watchEffect(() => {
  if (range.value && userIds.value && teamIds.value) fetchAnalytics();
});

useAnalyticsRefresh(fetchAnalytics, [range, userIds, teamIds]);
</script>
